import { plural, t } from "@lingui/macro";

export type Duration =
  | {
      hours?: number;
      minutes?: number;
      seconds?: number;
    }
  | number;

function parseDuration(duration: Duration): number {
  if (typeof duration === "number") {
    return duration;
  }

  const { hours = 0, minutes = 0, seconds = 0 } = duration;
  return hours * 3600 + minutes * 60 + seconds;
}

/**
 * Formats a media duration into a media-industry duration like 12:45
 *
 * @param duration - The duration to format.
 * @returns A formatted duration string (e.g., "01:30" for 1 minute 30 seconds,
 *          or "01:02:30" for 1 hour, 2 minutes, and 30 seconds).
 *
 * @example
 * formatMediaDuration({ hours: 1, minutes: 30 }); // "01:30"
 * formatMediaDuration({ hours: 1, minutes: 2, seconds: 30 }); // "01:02:30"
 * formatMediaDuration(90); // "01:30"
 * formatMediaDuration(3750); // "01:02:30"
 */
export function formatMediaDuration(duration: Duration) {
  const totalSeconds = parseDuration(duration);
  const sign = totalSeconds < 0 ? "-" : "";
  const absSeconds = Math.abs(totalSeconds);

  const formattedHours = Math.floor(absSeconds / 3600);
  const remainingMinutes = Math.floor((absSeconds % 3600) / 60);
  const remainingSeconds = Math.floor(absSeconds % 60);

  const formattedHoursStr = formattedHours.toString().padStart(2, "0");
  const formattedMinutesStr = remainingMinutes.toString().padStart(2, "0");
  const formattedSecondsStr = remainingSeconds.toString().padStart(2, "0");

  if (formattedHours > 0) {
    return `${sign}${formattedHoursStr}:${formattedMinutesStr}:${formattedSecondsStr}`;
  }

  return `${sign}${formattedMinutesStr}:${formattedSecondsStr}`;
}

/**
 * Formats a duration into a humand readable duration rounded to the biggest
 * unit like "3 hours"
 *
 * @param duration - The duration to format.
 * @returns A rounded duration string (e.g., "1 minute", "2 hours"), or `null`
 *          if the duration is negative.
 *
 * @example
 * formatRoundedDuration({ minutes: 5 }); // "5 minutes"
 * formatRoundedDuration(3600); // "1 hour"
 * formatRoundedDuration(-30); // null
 */
export function formatRoundedDuration(duration: Duration) {
  const totalSeconds = parseDuration(duration);

  if (totalSeconds < 0) return null;

  if (totalSeconds < 60) {
    return plural(totalSeconds, {
      one: "1 second",
      other: "# seconds",
    });
  }

  if (totalSeconds < 3600) {
    const minutes = Math.floor(totalSeconds / 60);
    return plural(minutes, {
      one: "1 minute",
      other: "# minutes",
    });
  }

  const hours = Math.floor(totalSeconds / 3600);
  return plural(hours, {
    one: "1 hour",
    other: "# hours",
  });
}

/**
 * Formats a duration into a human readable duration from now, like "3 hours ago"
 *
 * @param duration - The duration to format.
 * @returns A relative duration string (e.g., "around 5 minutes", "around an hour"),
 *          or `null` if the duration is negative.
 *
 * @example
 * formatRelativeDuration({ minutes: 5 }); // "around 5 minutes"
 * formatRelativeDuration(3600); // "around an hour"
 * formatRelativeDuration(30); // "less than a minute"
 * formatRelativeDuration(-10); // null
 */
export function formatRelativeDuration(duration: Duration) {
  const totalSeconds = parseDuration(duration);
  if (totalSeconds < 0) return null;

  if (totalSeconds < 60) {
    return t`less than a minute`;
  }

  if (totalSeconds < 3600) {
    const minutes = Math.floor(totalSeconds / 60);
    return plural(minutes, {
      one: "around a minute",
      other: "around # minutes",
    });
  }

  const hours = Math.floor(totalSeconds / 3600);
  return plural(hours, {
    one: "around an hour",
    other: "around # hours",
  });
}

/**
 * Converts a future date into a relative duration string.
 *
 * @param [date] - The date to compare against the current time.
 * @returns A relative duration string (e.g., "around 5 minutes", "around an hour"),
 *          or `null` if the date is in the past or not provided.
 *
 * @example
 * dateToRelativeDuration(new Date(Date.now() + 60000)); // "less than a minute"
 * dateToRelativeDuration("2025-01-01T12:00:00Z"); // "around X hours" (depending on the current time)
 * dateToRelativeDuration(null); // null
 * dateToRelativeDuration(new Date(Date.now() - 60000)); // null
 */
export function dateToRelativeDuration(date?: string | Date | null) {
  if (!date) return null;
  const updateDate = new Date(date);
  const now = new Date();
  if (updateDate < now) return null;
  const duration = updateDate.getTime() - now.getTime();

  return formatRelativeDuration(duration / 1000);
}
